/**
 * PhasePickerPopover
 *
 * Click-to-open popover for changing the phase of an incasso ticket.
 * Trigger is a colored phase badge. Content shows all available phases.
 * Uses the same colors as the kanban column headers.
 */

import { useState } from 'react';
import { Check } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from '@/lib/utils';
import { IncassoPhase } from '../shared/incassoTypes';
import { incassoPhaseColumns } from '../configs/incassoPhaseConfig';

interface PhasePickerPopoverProps {
  currentPhase: IncassoPhase;
  onChange: (newPhase: IncassoPhase) => void;
}

export const PhasePickerPopover = ({ currentPhase, onChange }: PhasePickerPopoverProps) => {
  const [open, setOpen] = useState(false);

  const currentCol = incassoPhaseColumns.find((c) => c.id === currentPhase);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium truncate max-w-[140px] border cursor-pointer transition-opacity hover:opacity-80',
            currentCol?.color ?? 'text-gray-700',
            currentCol?.headerBgColor ?? 'bg-gray-50',
            currentCol?.borderColor ?? 'border-gray-300',
          )}
          title={currentCol?.label ?? currentPhase}
        >
          {currentCol?.label ?? currentPhase}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-1"
        align="start"
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-medium text-neutral-500 px-2 py-1.5">
          Fase wijzigen
        </div>
        {incassoPhaseColumns.map((col) => {
          const isActive = col.id === currentPhase;
          return (
            <button
              key={col.id}
              onClick={() => {
                if (col.id !== currentPhase) {
                  onChange(col.id);
                }
                setOpen(false);
              }}
              className={cn(
                'flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-left transition-colors',
                isActive ? 'bg-neutral-100 font-medium' : 'hover:bg-neutral-50',
              )}
            >
              <span
                className={cn(
                  'inline-block w-2.5 h-2.5 rounded-full border flex-shrink-0',
                  col.headerBgColor,
                  col.borderColor,
                )}
              />
              <span className={cn('truncate', col.color)}>
                {col.label}
              </span>
              {isActive && (
                <Check size={14} weight="bold" className="ml-auto text-neutral-500 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
